/**
 * useAccessories Hook
 *
 * React Query hooks for accessories data fetching.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import {
    getAccessories,
    getAccessory,
    getAccessoriesForEquipmentType,
    checkAccessoryAvailability,
} from '@/api/accessories';
import {
    CustomerAccessorySearchRequest,
    CustomerAccessoryAvailabilityRequest,
} from '@/types';
import { useLocationStore } from '@/stores/useLocationStore';

/**
 * Hook for fetching accessories catalog
 */
export const useAccessories = (params: CustomerAccessorySearchRequest) => {
    return useQuery({
        queryKey: ['accessories', params],
        queryFn: () => getAccessories(params),
        placeholderData: (previousData) => previousData,
    });
};

/**
 * Hook for fetching a single accessory by ID
 */
export const useAccessory = (id: string | undefined, locationId?: string) => {
    return useQuery({
        queryKey: ['accessory', id, locationId],
        queryFn: () => getAccessory(id!, locationId),
        enabled: !!id,
    });
};

/**
 * Hook for fetching suggested accessories for an equipment type (cross-selling)
 */
export const useAccessoriesForEquipmentType = (
    equipmentTypeId: string | undefined,
    locationId?: string,
    take: number = 10
) => {
    return useQuery({
        queryKey: ['accessories-for-equipment', equipmentTypeId, locationId, take],
        queryFn: () => getAccessoriesForEquipmentType(equipmentTypeId!, locationId, take),
        enabled: !!equipmentTypeId,
        staleTime: 5 * 60 * 1000, // 5 minutes - accessories don't change frequently
    });
};

/**
 * Hook for checking accessory availability (mutation)
 */
export const useCheckAccessoryAvailability = () => {
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string;
            data: CustomerAccessoryAvailabilityRequest;
        }) => checkAccessoryAvailability(id, data),
    });
};

/**
 * Hook for checking accessory availability with auto-fetch
 * Similar to useAvailabilityQuery but for accessories
 */
export const useAccessoryAvailabilityQuery = (
    id: string | undefined,
    startDate: Date | undefined,
    endDate: Date | undefined,
    quantity: number = 1
) => {
    const { location } = useLocationStore();

    return useQuery({
        queryKey: ['accessory-availability', id, startDate, endDate, quantity, location?.id],
        queryFn: () =>
            checkAccessoryAvailability(id!, {
                startDate: startDate!.toISOString(),
                endDate: endDate!.toISOString(),
                quantity,
                locationId: location?.id,
            }),
        enabled: !!id && !!startDate && !!endDate,
        retry: false,
    });
};

export default {
    useAccessories,
    useAccessory,
    useAccessoriesForEquipmentType,
    useCheckAccessoryAvailability,
    useAccessoryAvailabilityQuery,
};
